/**
 * KioskCommunitySetting
 *
 * Entry point for configuring a community’s slideshow.
 * If the user isn’t logged in, it shows a login prompt; otherwise it loads
 * the modular settings UI.
 *
 * summary:
 * - Auth check via `useAuth`
 * - Renders `KioskSettingsMain` which handles all settings steps
 */

import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Container, CircularProgress } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";
import { refreshSession } from "../../services/authenticate";
import { KioskSettingsMain } from "./components/Settings";

/**
 * KioskCommunitySetting Component
 *
 * @returns {JSX.Element} The main kiosk settings component with auth checks
 */
export default function KioskCommunitySetting() {
  const { isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionChecked, setSessionChecked] = useState(false);

  // Validate Cognito session when the user appears logged in (cookie present).
  // Without this, expired tokens still look “logged in” until an API call fails.
  useEffect(() => {
    if (!isLoggedIn) {
      setSessionChecked(true);
      return;
    }

    setSessionChecked(false);
    let cancelled = false;

    (async () => {
      try {
        await refreshSession();
        if (!cancelled) setSessionChecked(true);
      } catch {
        if (!cancelled) {
          logout();
          setSessionChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, logout]);

  if (isLoggedIn && !sessionChecked) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            gap: 2,
          }}
        >
          <CircularProgress aria-label="Verifying session" />
          <Typography variant="body2" color="text.secondary">
            Verifying your session…
          </Typography>
        </Box>
      </Container>
    );
  }

  // If not logged in, show login prompt
  if (!isLoggedIn) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            textAlign: "center",
            gap: 3,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Login Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You need to be logged in with proper permissions to access kiosk
            settings.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() =>
              navigate("/auth", {
                state: {
                  from: {
                    pathname: location.pathname,
                    search: location.search,
                  },
                },
              })
            }
            sx={{ minWidth: 120 }}
          >
            Login
          </Button>
        </Box>
      </Container>
    );
  }

  // If logged in, render the settings component
  // The API will handle authorization checks and redirect to permission denied if needed
  return <KioskSettingsMain />;
}
